import type { PropertyDetailListing } from '@/src/components/features/property/types';
import type { SurroundingAreaStat, SurroundingAreaStats } from '@/src/types/listingDetails.type';

export type AreaStatShape = SurroundingAreaStat;

type FloorplanLike = {
  beds?: number;
};

type PricingListing = PropertyDetailListing & {
  floorplans?: Array<FloorplanLike>;
};

export type PricingComparisonKind = 'less' | 'more' | 'equal';

export type PricingComparisonDisplay = {
  kind: PricingComparisonKind;
  previewStat: AreaStatShape;
  areaEntries: Array<AreaStatShape>;
  listingPriceDisplay: string;
  beds?: number;
};

export const formatWholeNumber = (value: number): string => value.toLocaleString('en-US');

const getSurroundingAreaStats = (listing: PropertyDetailListing | null | undefined): SurroundingAreaStats | undefined =>
  listing?.areas?.surroundingAreaStats;

export const getPricingComparisonDisplay = (
  listing: PropertyDetailListing | null | undefined,
): PricingComparisonDisplay | null => {
  if (!listing || listing.active === false || listing.isAllowedToRetain === false) return null;
  if ((listing as PricingListing).showPricingStats !== true) return null;
  if ((listing as PricingListing).isApartmentBldg === true) return null;

  const stats = getSurroundingAreaStats(listing);
  const previewStat = stats?.neighborhood ?? stats?.city ?? stats?.county ?? null;
  if (!previewStat) return null;

  const minPrice = listing.listingMinMaxPriceBeds?.minPrice ?? listing.pricing?.summary?.priceLow ?? 0;
  const beds = (listing as PricingListing).floorplans?.[0]?.beds;
  const kind = previewStat.percentageValue < 0 ? 'less' : previewStat.percentageValue > 0 ? 'more' : 'equal';

  const areaEntries = [stats?.neighborhood, stats?.city, stats?.county, stats?.zip].filter(
    (stat): stat is AreaStatShape => stat != null,
  );

  return {
    kind,
    previewStat,
    areaEntries,
    listingPriceDisplay: `$${formatWholeNumber(minPrice)}`,
    beds,
  };
};
